import React, { useState, useCallback, useEffect } from 'react'
import type { DailyEventObject } from '@daily-co/daily-js'
import { useDaily } from '@daily-co/daily-react'

// This hook tells you, in a 1:1 Daily call:
// 1) the LOCAL user's connection status
// 2) the REMOTE user's connection status
// 3) who ended the call (local vs remote vs unknown)
//
// It does this by combining Daily's event model:
// - "local-only" events (they fire only on your client)
// - "remote-oriented" events (your client is informed about the other participant)
//
// ────────────────────────────────────────────────────────────────────────────────
// HOW DAILY WORKS:
// - Daily exposes participant snapshots via call.participants(), and emits events
//   as things change (someone joins/leaves, quality changes, network link hiccups).
//
// RELEVANT EVENTS WE USE (and why):
// - 'network-quality-change' (LOCAL ONLY):
//     Fires when Daily detects a change in YOUR (local) network quality.
//     Payload includes a coarse 'networkState' of 'good' | 'warning' | 'bad' and
//     sometimes reasons. There's no per-remote version of this event.
// - 'participant-updated' (REMOTE + LOCAL):
//     Fires when a participant object changes. We watch it mainly for the REMOTE
//     user's 'networkQualityState' (same coarse values) so we can map their status.
//     It will also fire for the local participant; we use that as a backup signal.
// - 'participant-left' (REMOTE from your POV):
//     Fires when the remote leaves/disconnects (your client sees them go away).
//     That’s our strongest "remote ended" signal.
// - 'left-meeting' (LOCAL ONLY):
//     Fires on your client when YOU leave (hang up via API/UI). That’s our strongest
//     "local ended" signal.
// - (optional) 'joined-meeting' / 'participant-joined':
//     We use these to seed initial state immediately on join.
// - (optional) 'network-connection':
//     Link-level “interrupted/connected” events. Helpful for deep debugging,
//     but we *don’t* need it to compute the simple statuses—so we skip updating
//     core state from it to keep the logic minimal.
//
//
// STATE MAPPING POLICY (to keep it simple, as requested):
// - 'good' from Daily  → 'good'
// - 'warning'/'bad'    → 'bad'   (we collapse into one bucket)
// - no data yet        → 'n/a'
// - left/disconnected  → 'disconnected'
// - endedBy:
//     'left-meeting' first  → 'local'
//     'participant-left' first → 'remote'
//     otherwise → 'unknown'
//
// ────────────────────────────────────────────────────────────────────────────────

type ConnectionStatus = 'n/a' | 'good' | 'bad' | 'disconnected'
type EndedBy = 'provider' | 'patient' | 'unknown'

type CallStatus = {
  overall: ConnectionStatus
  provider: ConnectionStatus
  patient: ConnectionStatus
  endedBy: undefined | EndedBy
}

function getWorstStatus(status1: ConnectionStatus, status2: ConnectionStatus) {
  const priority = { disconnected: 4, bad: 3, good: 2, 'n/a': 1 }
  return priority[status1] > priority[status2] ? status1 : status2
}

function mapQualityToConnection(quality?: string) {
  // Daily's coarse quality values: 'good' | 'warning' | 'bad' | undefined
  if (quality === 'good') return 'good'
  if (quality === 'warning' || quality === 'bad') return 'bad'
  return 'n/a'
}

export type UseParticipantConnectionStatusesReturnType = ReturnType<typeof useParticipantConnectionStatuses>

const initialState: CallStatus = { overall: 'n/a', provider: 'n/a', patient: 'n/a', endedBy: undefined }

// *NOTE: endedBy is solely for dev purposes at the moment. it's not used in the app. Additionally connection status per
// user is seemingly not fully accurate. only used for messaging now, but may need further testing over a real
// connection instead of throttling to determine if per-user status knowledge is viable.
export function useParticipantConnectionStatuses() {
  const [participantConnectionStatuses, setParticipantConnectionStatuses] = useState<CallStatus>(initialState)

  const updateStateWithOverallCalculated = React.useCallback((input: CallStatus | ((s: CallStatus) => CallStatus)) => {
    setParticipantConnectionStatuses((s) => {
      const next = typeof input === 'function' ? input(s) : input
      return {
        ...next,
        overall: getWorstStatus(next.provider, next.patient),
      }
    })
  }, [])

  const callObject = useDaily()

  // Helper: seed initial local/remote from the current participants snapshot
  const init = useCallback(() => {
    if (!callObject) return
    const participants = callObject.participants()

    const provider = mapQualityToConnection(participants.local?.networkQualityState)
    const remoteParticipant = Object.values(participants).find((p) => !p.local && !!p.session_id)

    const patient = mapQualityToConnection(remoteParticipant?.networkQualityState)

    updateStateWithOverallCalculated((s) => ({
      ...s,
      provider,
      patient,
    }))
  }, [callObject, updateStateWithOverallCalculated])

  useEffect(() => {
    if (!callObject) return

    // ──────────────────────────────────────────────────────────────────
    // LOCAL-ONLY QUALITY: 'network-quality-change'
    // Fires when Daily detects a threshold change for *your* network.
    // We treat this as the most accurate/fast local signal.
    // ──────────────────────────────────────────────────────────────────
    const onLocalNetworkQuality = (ev: DailyEventObject<'network-quality-change'>) => {
      const localState = mapQualityToConnection(ev.networkState)
      updateStateWithOverallCalculated((s) => ({ ...s, provider: localState }))
    }

    // ──────────────────────────────────────────────────────────────────
    // PARTICIPANT SNAPSHOT CHANGES: 'participant-updated'
    // Fires for both local and remote. We mainly care about the REMOTE user's
    // .networkQualityState here. If it's the local participant, we accept it
    // as a backup/secondary source (in case the local event didn't fire yet).
    // ──────────────────────────────────────────────────────────────────
    const onParticipantUpdated = (ev: DailyEventObject<'participant-updated'>) => {
      const { local, networkQualityState } = ev.participant
      if (local) {
        // Backup path for local quality—events can arrive in different orders.
        updateStateWithOverallCalculated((s) => ({
          ...s,
          provider: mapQualityToConnection(networkQualityState),
        }))
      } else {
        updateStateWithOverallCalculated((s) => ({
          ...s,
          patient: mapQualityToConnection(networkQualityState),
        }))
      }
    }

    // ──────────────────────────────────────────────────────────────────
    // REMOTE LEFT: 'participant-left'
    // When your client observes the remote leaving, that's the definitive
    // "remote ended" signal from your perspective.
    // ──────────────────────────────────────────────────────────────────
    const onParticipantLeft = () => {
      updateStateWithOverallCalculated((s) => ({
        ...s,
        patient: 'disconnected',
        endedBy: 'patient',
      }))
    }

    // ──────────────────────────────────────────────────────────────────
    // LOCAL LEFT: 'left-meeting'
    // This fires only on YOUR client when YOU leave (hang up).
    // That's definitive "local ended".
    // ──────────────────────────────────────────────────────────────────
    const onLeftMeeting = () => {
      updateStateWithOverallCalculated((s) => ({
        ...s,
        provider: 'disconnected',
        endedBy: 'provider',
      }))
    }

    // Also seed now in case we attach after join (e.g., hot reload)
    init()

    // Attach listeners
    callObject.on('network-quality-change', onLocalNetworkQuality)
    callObject.on('participant-updated', onParticipantUpdated)
    callObject.on('participant-left', onParticipantLeft)
    callObject.on('left-meeting', onLeftMeeting)
    callObject.on('joined-meeting', init)

    return () => {
      callObject.off('network-quality-change', onLocalNetworkQuality)
      callObject.off('participant-updated', onParticipantUpdated)
      callObject.off('participant-left', onParticipantLeft)
      callObject.off('left-meeting', onLeftMeeting)
      callObject.off('joined-meeting', init)
    }
  }, [callObject, init, updateStateWithOverallCalculated])

  // If both went "disconnected" without a clear initiator, default to 'unknown'
  useEffect(() => {
    if (
      !participantConnectionStatuses.endedBy &&
      participantConnectionStatuses.provider === 'disconnected' &&
      participantConnectionStatuses.patient === 'disconnected'
    ) {
      updateStateWithOverallCalculated((s) => ({ ...s, endedBy: 'unknown' }))
    }
  }, [
    participantConnectionStatuses.provider,
    participantConnectionStatuses.patient,
    participantConnectionStatuses.endedBy,
    updateStateWithOverallCalculated,
  ])

  return { participantConnectionStatuses }
}
